import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { debounceTime, distinctUntilChanged, map, Subscription, switchMap, tap } from 'rxjs';
import { Button } from 'primeng/button';
import { TabsModule } from 'primeng/tabs';
import { CarsList } from '../cars-list/cars-list';
import { ModelsListComponent } from '../../models/models-list-component/models-list-component';
import { Car } from '../../../../models/car';
import { Params } from '../../../../services/utilis/add-params-service';
import { FormControl } from '@angular/forms';
import { CarService } from '../../../../services/vehicles/car-service';
import { PaginatorState } from 'primeng/paginator';
import { CustomFilter } from '../../../../@shared/components/table-component/table-component';
import { Employee } from '../../../../models/employee';
import { UserRole } from '../../../../models/enums';
import { AddCarForm } from '../../../../@shared/forms/vehicles/cars/add-car-form/add-car-form';
import { EmployeeService } from '../../../../services/employees/employee-service';

@Component({
  selector: 'app-cars-component',
  imports: [Button, TabsModule, CarsList, AddCarForm, ModelsListComponent],
  templateUrl: './cars-component.html',
  styleUrl: './cars-component.scss'
})
export class CarsComponent implements OnInit, OnDestroy {

  private carListSub: Subscription;

  modelsFilter: CustomFilter = {
    key: "modelId",
    label: "Model",
    type: "multiselect",
    options: []
  }


  cars = signal<Car[]>([]);
  totalRecords = signal(0);
  availableCarsCount = signal(0);
  modelsCount = signal(0);
  rows = 20;
  first = 0;

  activeTab = signal('0');
  modelsLoaded = false;

  searchControl = new FormControl('');

  employee = signal<Employee | undefined>(undefined);
  UserRole = UserRole

  addCarDialog = false

  params = [
    "page",
    "size",
    "search",
    "id",
    "sortBy",
    "sortDirection",
    "minFuelLevel",
    "maxFuelLevel",
    "needsRebalancing",
    "modelId",
    "status",
    "rentalType"
  ];

  modifiedParams: Params = {
    page: "page",
    size: "size",
    search: "search",
    sortBy: "sortBy",
    sortDirection: "sortDirection",
    minFuelLevel: "minFuelLevel",
    maxFuelLevel: "maxFuelLevel",
    needsRebalancing: "needsRebalancing",
    modelId: "modelId",
    status: "status",
    rentalType: "rentalType"
  };

  private carService = inject(CarService);
  private employeeService = inject(EmployeeService)
  router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);

  ngOnInit() {
    this.setActiveTabFromQueryParams();
    this.getEmployee();
    this.loadStats();
    if (this.activeTab() === 'models') {
      this.modelsLoaded = true;
    }
    if(!this.cars()?.length) {
      this.getCarsList();
    }
  }

  loadStats() {
    this.carService.getCarsCounts().subscribe(res => {
      this.availableCarsCount.set(res.data.availableCars);
      this.modelsCount.set(res.data.totalModels);
    });
  }

  getCarsList = () => {
    this.carListSub?.unsubscribe();
    this.carListSub = this.activatedRoute.queryParams.pipe(
      map((params) =>
        Object.fromEntries(
          Object.entries(params)
            .filter(([key]) => this.params.includes(key))
            .map(([key, value]) => [this.modifiedParams[key] || key, value])
        )
      ),

      distinctUntilChanged((prev, curr) =>
        JSON.stringify(prev) === JSON.stringify(curr)
      ),

      tap((queryParams) => {
        this.searchControl.setValue(queryParams['search'], { emitEvent: false });
      }),

      switchMap((queryParams) => {

        const isMapTab = this.activeTab() === '1';

        const params = {
          ...queryParams,
          size: isMapTab ? 10000 : (queryParams['size'] ?? 20),
          sortBy: queryParams['sortBy'] ?? 'updatedAt',
          sortDirection: queryParams['sortDirection'] ?? 'DESC'
        };

        return this.carService.getCarsList(params);

      })

    ).subscribe(res => {
      this.cars.set(res.data.content);
      this.totalRecords.set(res.data.totalElements);
    });
  }

  ngOnDestroy() {
    this.carListSub?.unsubscribe();
  }

  getEmployee = () => {
    this.employee = this.employeeService.employee;
  }

  onPageChange(event: PaginatorState) {

    this.router.navigate([], {
      queryParams: {
        page: event.page,
        size: event.rows
      },
      queryParamsHandling: 'merge'
    });

  }

  onSearchChange = () => {

    this.searchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(value => {

      this.router.navigate([], {
        queryParams: {
          search: value,
          page: undefined
        },
        queryParamsHandling: 'merge'
      });

    });

  }

  onTabChange(tab: string | number | undefined) {

    const tabValue = String(tab);
    this.activeTab.set(tabValue);

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { tab: tabValue },
      queryParamsHandling: ''
    });

    if (tabValue === 'models') {
      this.modelsLoaded = true;
    } else {
      this.getCarsList();
    }
  }

  private setActiveTabFromQueryParams() {
    const tabFromUrl = this.activatedRoute.snapshot.queryParamMap.get('tab');
    if (tabFromUrl) {
      this.activeTab.set(tabFromUrl);
    } else {
      this.activeTab.set('0');
    }
  }

  openAddCarDialog = () => {
    this.addCarDialog = true
  }

  onCarAdded = () => {
    this.getCarsList();
    this.addCarDialog = false
  }
}
